// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useMemo, useCallback } from 'react'
import type { JSONSchema } from '@/api/types/schema'
import type { SchemaBuilderSection, SchemaBuilderState } from '@/types/schema-builder'
import type { SchemaField, SchemaFieldType } from '@/types/schema-fields'
import { 
  generateSchemaFromSections, 
  extractSectionsFromSchema, 
  createNewSection, 
  createNewField,
  createDefaultSummarySection
} from '@/utils/schema/schema-utils'
import { isValidSchemaType } from '@/types/schema-registry'
import { v4 as uuidv4 } from 'uuid'
import { toast } from '@/hooks/use-toast'

interface UseSchemaStateProps {
  currentSchema?: JSONSchema
  schema_type: string
  key?: string // Key prop to force re-initialization
}

export function useSchemaState({ 
  currentSchema, 
  schema_type,
  key
}: UseSchemaStateProps) {
  // Validate schema type exists in registry
  if (!isValidSchemaType(schema_type)) {
    console.warn(`Invalid schema type: ${schema_type}. Falling back to monitor.`)
  }
  
  // Create a stable function to get initial state
  const getInitialState = useCallback((schema?: JSONSchema, type?: string): SchemaBuilderState => {
    console.log('[useSchemaState] Generating initial state for:', { schema_type: type || schema_type, hasSchema: !!schema })
    
    const actualSchema = schema !== undefined ? schema : currentSchema
    const actualType = type || schema_type
    
    const sections = actualSchema ? extractSectionsFromSchema(actualSchema) : 
                     actualType === 'monitor' ? [createDefaultSummarySection()] : []
    const dialogTitle = String(actualSchema?.title || 'Analysis Results')
    
    // For monitor schema, ensure summary section with filter_score exists
    const processedSections = ensureDefaultSummarySection(sections, actualType)
    
    return {
      sections: processedSections,
      dialogTitle
    }
  }, [schema_type]) // Only depend on schema_type, not currentSchema
  
  // Schema generator state - will re-initialize when key changes
  const [generatorState, setGeneratorState] = useState<SchemaBuilderState>(() => getInitialState(currentSchema, schema_type))

  // Reset state only when key changes (workspace or schema type switch)
  useEffect(() => {
    console.log('[useSchemaState] Resetting state due to key change:', { key, hasSchema: !!currentSchema })
    setGeneratorState(getInitialState(currentSchema, schema_type))
  }, [key, getInitialState]) // Only reset when key changes, not when currentSchema changes


  // Generate schema from current state
  const generatedSchema = useMemo(() => {
    if (generatorState.sections.length === 0) {
      return null
    }
    return generateSchemaFromSections(generatorState.sections, generatorState.dialogTitle)
  }, [generatorState.sections, generatorState.dialogTitle])

  // Remove automatic propagation - parent will read generatedSchema when needed
  // This prevents auto-saving on every change

  // Sort sections by order field
  const sortedSections = useMemo(() => {
    return [...generatorState.sections].sort((a, b) => a.order - b.order)
  }, [generatorState.sections])

  const handleSectionChange = useCallback((sectionUuid: string, updates: Partial<SchemaBuilderSection>) => {
    setGeneratorState(prevState => {
      const updatedSections = prevState.sections.map(section => 
        section.uuid === sectionUuid 
          ? { ...section, ...updates }
          : section
      )
      
      return {
        ...prevState,
        sections: updatedSections
      }
    })
  }, [])


  const handleDialogTitleChange = useCallback((dialogTitle: string) => {
    setGeneratorState(prev => ({
      ...prev,
      dialogTitle
    }))
  }, [])

  const addSection = useCallback(() => {
    // Use functional update to ensure we have the latest state
    setGeneratorState(prevState => {
      const newSection = createNewSection('New Section', prevState.sections)
      
      return {
        ...prevState,
        sections: [...prevState.sections, newSection]
      }
    })
  }, [])

  const removeSection = useCallback((sectionId: string) => {
    setGeneratorState(prevState => {
      // Prevent removal of summary section for monitor schema
      if (sectionId === 'summary' && schema_type === 'monitor') {
        toast({
          title: "Cannot Remove Summary Section",
          description: "The summary section is required for monitor schemas and cannot be removed.",
          variant: "destructive"
        })
        return prevState
      }
      
      const updatedSections = prevState.sections.filter(section => section.id !== sectionId)
      
      return {
        ...prevState,
        sections: updatedSections
      }
    })
  }, [schema_type])

  const duplicateSection = useCallback((sectionId: string) => {
    setGeneratorState(prevState => {
      const sectionToDuplicate = prevState.sections.find(s => s.id === sectionId)
      if (!sectionToDuplicate) return prevState
      
      const maxOrder = Math.max(...prevState.sections.map(s => s.order), 0)
      const newId = `${sectionToDuplicate.id}_copy_${Date.now()}`
      const newTitle = `${sectionToDuplicate.title} (Copy)`
      const newOrder = maxOrder + 1
      
      const duplicatedSection: SchemaBuilderSection = {
        ...sectionToDuplicate,
        uuid: uuidv4(), // Generate new UUID for the copy
        id: newId,
        title: newTitle,
        order: newOrder,
        // Deep copy fields with new UUIDs
        fields: sectionToDuplicate.fields.map(field => ({
          ...field,
          uuid: uuidv4()
        }))
      }
      
      return {
        ...prevState,
        sections: [...prevState.sections, duplicatedSection]
      }
    })
  }, [])

  const addField = useCallback((sectionUuid: string, field_type: SchemaFieldType) => {
    setGeneratorState(prevState => {
      const section = prevState.sections.find(s => s.uuid === sectionUuid)
      if (!section) return prevState

      const newField = createNewField(field_type, 'New Field', section.fields)

      // Set hardcoded ID for the first score field in summary section of monitor schema
      if (field_type === 'score' && 
          section.id === 'summary' && 
          schema_type === 'monitor' &&
          !section.fields.some(f => f.id === 'filter_score')) {
        newField.id = 'filter_score'
      }
      
      const updatedSections = prevState.sections.map(s => 
        s.uuid === sectionUuid 
          ? { ...s, fields: [...s.fields, newField] }
          : s
      )
      
      return {
        ...prevState,
        sections: updatedSections
      }
    })
  }, [])

  const removeField = useCallback((sectionUuid: string, fieldUuid: string) => {
    setGeneratorState(prevState => {
      const section = prevState.sections.find(s => s.uuid === sectionUuid)
      if (!section) return prevState
      
      const fieldToRemove = section.fields.find(f => f.uuid === fieldUuid)
      
      // Prevent removal of the required score field (with ID 'filter_score') from summary section in monitor schema
      if (section.id === 'summary' &&
          fieldToRemove?.field_type === 'score' &&
          fieldToRemove?.id === 'filter_score' &&
          schema_type === 'monitor') {
        toast({
          title: "Cannot Remove Required Score Field",
          description: "The score field with ID 'filter_score' is required in the summary section for monitor schemas.",
          variant: "destructive"
        })
        return prevState
      }
      
      const updatedFields = section.fields.filter(field => field.uuid !== fieldUuid)
      
      const updatedSections = prevState.sections.map(s => 
        s.uuid === sectionUuid 
          ? { ...s, fields: updatedFields }
          : s
      )
      
      return {
        ...prevState,
        sections: updatedSections
      }
    })
  }, [schema_type])

  const updateField = useCallback((sectionUuid: string, fieldUuid: string, updates: Partial<SchemaField>) => {
    setGeneratorState(prevState => {
      const section = prevState.sections.find(s => s.uuid === sectionUuid)
      if (!section) return prevState
      
      const updatedFields = section.fields.map(field =>
        field.uuid === fieldUuid ? { ...field, ...updates } : field
      )
      
      const updatedSections = prevState.sections.map(s => 
        s.uuid === sectionUuid 
          ? { ...s, fields: updatedFields }
          : s
      )
      
      return {
        ...prevState,
        sections: updatedSections
      }
    })
  }, [])

  const moveFieldUp = useCallback((sectionUuid: string, fieldUuid: string) => {
    setGeneratorState(prevState => {
      const section = prevState.sections.find(s => s.uuid === sectionUuid)
      if (!section) return prevState
      
      const field_index = section.fields.findIndex(f => f.uuid === fieldUuid)
      if (field_index === -1 || field_index === 0) return prevState // Can't move up if already at top
      
      // Create new fields array with field moved up
      const updatedFields = [...section.fields]
      const [field_to_move] = updatedFields.splice(field_index, 1)
      updatedFields.splice(field_index - 1, 0, field_to_move)
      
      // Update order values to match new positions
      const reorderedFields = updatedFields.map((field, index) => ({
        ...field,
        order: index
      }))
      
      const updatedSections = prevState.sections.map(s => 
        s.uuid === sectionUuid 
          ? { ...s, fields: reorderedFields }
          : s
      )
      
      return {
        ...prevState,
        sections: updatedSections
      }
    })
  }, [])

  const moveFieldDown = useCallback((sectionUuid: string, fieldUuid: string) => {
    setGeneratorState(prevState => {
      const section = prevState.sections.find(s => s.uuid === sectionUuid)
      if (!section) return prevState
      
      const field_index = section.fields.findIndex(f => f.uuid === fieldUuid)
      if (field_index === -1 || field_index === section.fields.length - 1) return prevState // Can't move down if already at bottom
      
      // Create new fields array with field moved down
      const updatedFields = [...section.fields]
      const [field_to_move] = updatedFields.splice(field_index, 1)
      updatedFields.splice(field_index + 1, 0, field_to_move)
      
      // Update order values to match new positions
      const reorderedFields = updatedFields.map((field, index) => ({
        ...field,
        order: index
      }))
      
      const updatedSections = prevState.sections.map(s => 
        s.uuid === sectionUuid 
          ? { ...s, fields: reorderedFields }
          : s
      )
      
      return {
        ...prevState,
        sections: updatedSections
      }
    })
  }, [])

  const moveSectionUp = useCallback((sectionUuid: string) => {
    setGeneratorState(prevState => {
      const section_index = prevState.sections.findIndex(s => s.uuid === sectionUuid)
      if (section_index === -1 || section_index === 0) return prevState // Can't move up if already at top
      
      // Create new sections array with section moved up
      const updatedSections = [...prevState.sections]
      const [section_to_move] = updatedSections.splice(section_index, 1)
      updatedSections.splice(section_index - 1, 0, section_to_move)
      
      // Update order values to match new positions
      const reorderedSections = updatedSections.map((section, index) => ({
        ...section,
        order: index
      }))
      
      return {
        ...prevState,
        sections: reorderedSections,
      }
    })
  }, [])

  const moveSectionDown = useCallback((sectionUuid: string) => {
    setGeneratorState(prevState => {
      const section_index = prevState.sections.findIndex(s => s.uuid === sectionUuid)
      if (section_index === -1 || section_index === prevState.sections.length - 1) return prevState // Can't move down if already at bottom
      
      // Create new sections array with section moved down
      const updatedSections = [...prevState.sections]
      const [section_to_move] = updatedSections.splice(section_index, 1)
      updatedSections.splice(section_index + 1, 0, section_to_move)
      
      // Update order values to match new positions
      const reorderedSections = updatedSections.map((section, index) => ({
        ...section,
        order: index
      }))
      
      return {
        ...prevState,
        sections: reorderedSections,
      }
    })
  }, [])

  return {
    generatorState,
    sortedSections,
    generatedSchema,
    handleSectionChange,
    handleDialogTitleChange,
    addSection,
    removeSection,
    duplicateSection,
    addField,
    removeField,
    updateField,
    moveFieldUp,
    moveFieldDown,
    moveSectionUp,
    moveSectionDown
  }
}

/**
 * Ensure monitor schemas have a default summary section with score field
 */
function ensureDefaultSummarySection(sections: SchemaBuilderSection[], schema_type: string): SchemaBuilderSection[] {
  if (schema_type !== 'monitor') {
    return sections
  }
  
  const hasSummarySection = sections.some(s => s.id === 'summary')
  
  if (!hasSummarySection) {
    const summarySection = createDefaultSummarySection()
    return [summarySection, ...sections]
  }
  
  // Check if summary section has the required score field with ID 'filter_score'
  const summarySection = sections.find(s => s.id === 'summary')
  if (summarySection && !summarySection.fields.some(f => f.field_type === 'score' && f.id === 'filter_score')) {
    const score_field = createNewField('score', 'Filter Score', summarySection.fields)
    score_field.id = 'filter_score'
    score_field.title = 'Filter Score'
    score_field.description = 'Overall scoring and score_type classification for this analysis'
    
    const updatedSummarySection = {
      ...summarySection,
      fields: [...summarySection.fields, score_field]
    }
    
    return sections.map(s => s.id === 'summary' ? updatedSummarySection : s)
  }
  
  return sections
}
